(function () {
  'use strict';
  if (customElements.get('bolodoo-cart-event-date')) return;

  const selector = 'bolodoo-cart-event-date';
  let selected;
  let saved;
  let pending = null;
  let failed = false;
  let checkoutWaiting = false;
  let checkoutReady = false;
  let needsSync = false;
  const services = new Set([
    'plus-size-custom-size-service-fee-dont-remove-it-if-you-choose-this-service',
    'rush-order-service-fee-dont-remove-it-if-you-choose-the-rush-service',
    'shipping-protection',
    'product-protection',
  ]);
  const endpoint = suffix => `${(window.Shopify?.routes?.root || '/').replace(/\/?$/, '/')}cart${suffix}`;
  const enqueue = action => {
    const job = (window.__bolodooCartMutationTail || Promise.resolve()).then(action, action);
    window.__bolodooCartMutationTail = job.catch(() => {});
    return job;
  };
  const formatted = value => value ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${value}T00:00:00Z`)) : '';
  const expected = (item, label) => services.has(item.handle || (item.url || '').split('/products/')[1]?.split('?')[0]) ? '' : label;

  async function request(suffix, body) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(endpoint(suffix), {
        method: body ? 'POST' : 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });
      if (!response.ok) throw new Error('Event date save failed');
      return await response.json();
    } finally { clearTimeout(timer); }
  }

  function refreshRows(cart) {
    const parse = html => new DOMParser().parseFromString(html, 'text/html');
    // Replace only item rows so the date picker keeps its focus and current input.
    const drawer = document.getElementById('CartDrawer-CartItems');
    if (drawer && cart.sections?.['cart-drawer']) {
      const next = parse(cart.sections['cart-drawer']).getElementById('CartDrawer-CartItems');
      if (next) {
        const notification = drawer.closest('.notification-open');
        const activeRows = [...drawer.querySelectorAll('.cart-item.active')].map(row => ({
          id: row.id, variant: row.dataset.variantId, group: row.dataset.bndGroup,
        }));
        drawer.innerHTML = next.innerHTML;
        if (notification) {
          const rows = [...drawer.querySelectorAll('.cart-item')];
          // Date properties change line keys; the fee group and variant stay stable.
          activeRows.forEach(active => {
            const candidates = rows.filter(row => row.dataset.variantId === active.variant && row.dataset.bndGroup === active.group);
            const row = candidates.find(row => row.id === active.id) || candidates[0];
            row?.classList.add('active');
          });
          if (!drawer.querySelector('.cart-item.active')) notification.classList.remove('notification-open');
        }
      }
    }
    const full = document.querySelector('#main-cart-items .js-contents');
    if (full && cart.sections?.['main-cart-items']) {
      const next = parse(cart.sections['main-cart-items']).querySelector('.js-contents');
      if (next) full.innerHTML = next.innerHTML;
    }
  }

  function render() {
    document.querySelectorAll(selector).forEach(field => {
      const input = field.querySelector('input[type="date"]');
      if (input && !input.validity.badInput && input.value !== selected) input.value = selected;
      field.setAttribute('aria-busy', String(Boolean(pending)));
      const error = field.querySelector('[data-event-date-error]');
      if (error) {
        error.hidden = !failed;
        error.textContent = failed ? 'Your event date could not be saved. Please try again before checkout.' : '';
      }
      const retry = field.querySelector('[data-event-date-retry]');
      if (retry) retry.hidden = !failed;
    });
  }

  function persist() {
    if (pending) return pending;
    if (selected === saved && !needsSync) {
      failed = false;
      render();
      return Promise.resolve(true);
    }
    // Serialize edits so an older response cannot replace a more recent date.
    pending = enqueue(async () => {
      try {
        while (selected !== saved || needsSync) {
          needsSync = false;
          const value = selected;
          const label = formatted(value);
          let cart = await request('.js');
          let changed = false;
          let item;
          while ((item = cart.items.find(line => (line.properties?.['Event Date'] || '') !== expected(line, label)))) {
            const properties = { ...item.properties, 'Event Date': expected(item, label) };
            // Properties replace the whole object. Preserve sizes, uploads and private fee groups.
            cart = await request('/change.js', { id: item.key, quantity: item.quantity, properties });
            changed = true;
          }
          if ((cart.attributes?.['Event Date'] || '') !== value || changed) cart = await request('/update.js', {
            attributes: { 'Event Date': value },
            sections: ['cart-drawer', 'main-cart-items'],
            sections_url: window.location.pathname,
          });
          if ((cart.attributes?.['Event Date'] || '') !== value || cart.items.some(line => (line.properties?.['Event Date'] || '') !== expected(line, label))) throw new Error('Event date readback mismatch');
          if (changed) refreshRows(cart);
          saved = value;
        }
        failed = false;
        return true;
      } catch {
        needsSync = true;
        failed = true;
        return false;
      } finally {
        pending = null;
        render();
      }
    });
    render();
    return pending;
  }

  customElements.define('bolodoo-cart-event-date', class extends HTMLElement {
    connectedCallback() {
      const input = this.querySelector('input[type="date"]');
      if (!input) return;
      if (selected === undefined) {
        selected = saved = input.value;
        if (selected) { needsSync = true; persist(); }
      }
      if (!this.initialized) {
        this.initialized = true;
        const onChange = () => {
          if (!input.validity.valid) return;
          selected = input.value;
          failed = false;
          persist();
        };
        input.addEventListener('input', onChange);
        input.addEventListener('change', onChange);
        this.querySelector('[data-event-date-retry]')?.addEventListener('click', () => persist());
      }
      render();
    }
  });

  function beforeCheckout(event, control, form) {
    const field = document.querySelector(`${selector} input[type="date"]`);
    if (!field) return;
    if (!field.validity.valid) {
      event.preventDefault();
      event.stopImmediatePropagation();
      field.reportValidity();
      return;
    }
    if (checkoutReady && !pending && selected === saved && !failed) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutWaiting) return;
    checkoutWaiting = true;
    needsSync = true;
    persist().then(ok => {
      checkoutWaiting = false;
      if (!ok) {
        document.querySelector(`${selector} input[type="date"]`)?.focus();
        return;
      }
      checkoutReady = true;
      try {
        if (control) {
          const current = control.isConnected ? control : [...document.querySelectorAll('cart-drawer .cart__checkout-button, button[name="checkout"]')].find(button => button.getClientRects().length);
          current?.click();
        } else if (form.isConnected) {
          const submitter = document.getElementById('CartDrawer-Checkout');
          form.requestSubmit(submitter || undefined);
        }
      } finally { checkoutReady = false; }
    });
  }

  // Window capture runs before existing theme and shipping-protection checkout handlers.
  window.addEventListener('click', event => {
    const control = event.target.closest?.('cart-drawer .cart__checkout-button, button[name="checkout"], input[name="checkout"], a[href*="/checkout"]');
    if (control) beforeCheckout(event, control);
  }, true);
  window.addEventListener('submit', event => {
    if (event.target.id === 'CartDrawer-Form' || event.submitter?.name === 'checkout') beforeCheckout(event, event.submitter, event.target);
  }, true);
  document.addEventListener('cart:change', event => {
    const cart = event.detail?.cart || event.detail;
    if (selected === undefined || !cart?.items) return;
    const label = formatted(selected);
    if (cart.items.some(item => (item.properties?.['Event Date'] || '') !== expected(item, label))) {
      needsSync = true;
      persist();
    }
  });
})();
